import axios from 'axios';
import { getAccessToken, setAccessToken } from './tokenStore';

// withCredentials: the refresh token lives in an httpOnly cookie set by the
// backend, so the browser must attach it on auth requests (refresh/logout).
const api = axios.create({ baseURL: '/api', timeout: 120000, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function logout() {
  setAccessToken(null);
  const themeMode = localStorage.getItem('theme-mode');
  localStorage.clear();
  if (themeMode) localStorage.setItem('theme-mode', themeMode);
  window.location.href = '/login';
}

// Single-flight refresh: concurrent 401s share ONE refresh request. The backend
// rotates the refresh token (ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION)
// and re-sets it in the httpOnly cookie, so we must not trigger it multiple
// times in parallel. The refresh token is never read by JS — it rides along as
// a cookie thanks to withCredentials.
let refreshPromise = null;

// This guard is per-tab (module scope). Cookies are shared across tabs, so two
// tabs refreshing at the same instant leave the slower one presenting a token
// the faster one already rotated away (401). One retry re-reads the cookie the
// other tab just received. Mitigation, not a guarantee: if the peer's response
// has not landed yet, the retry replays the same stale token.
const ROTATION_RACE_RETRY_MS = 250;

function postRefresh() {
  return axios
    .post('/api/auth/refresh/', {}, { withCredentials: true })
    .then(({ data }) => data.access);
}

export function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = postRefresh()
    .catch((err) => {
      // Retry only on 401 (token rotated or expired). A 400 means no cookie at
      // all — nothing a second request could fix.
      if (err.response?.status !== 401) throw err;
      return new Promise((resolve) => setTimeout(resolve, ROTATION_RACE_RETRY_MS)).then(
        postRefresh
      );
    })
    .then((access) => {
      setAccessToken(access);
      return access;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (
      err.response?.status === 401 &&
      original &&
      !original._retry &&
      // We only attempt a refresh if there was an active session (access token
      // present). The refresh token itself is the httpOnly cookie.
      getAccessToken()
    ) {
      original._retry = true; // prevents any refresh loop
      try {
        const access = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${access}`;
        return api(original);
      } catch {
        logout();
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

export default api;
